import { useEffect, useRef, useState } from 'react';
import type React from 'react';

import { cn } from '@change/ds-utils';

import { checkReduceMotion } from '../../utils';

import { checkScrollVisibility, findScrollTargets } from './utils';

const DEFAULT_INTERVAL_SECONDS = 3;

type Props = {
	intervalSeconds?: number;
	className?: string;
	children?: React.ReactNode;
};

export const ActivityScroller: React.FC<Props> = ({
	intervalSeconds = DEFAULT_INTERVAL_SECONDS,
	children,
	className,
	...rest
}) => {
	const [shouldAnimate, setShouldAnimate] = useState(() => !checkReduceMotion());
	const [showGradient, setShowGradient] = useState(true);
	const scrollerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!scroller) {
			return () => {};
		}

		const onScroll = () => {
			const { next, last } = findScrollTargets(scroller);

			if (last && checkScrollVisibility(scroller, last).left && checkScrollVisibility(scroller, last).right) {
				if (showGradient) {
					setShowGradient(false);
				}
				return;
			}

			if (next && checkScrollVisibility(scroller, next).left && !showGradient) {
				setShowGradient(true);
			}
		};

		scroller.addEventListener('scroll', onScroll);
		return () => scroller.removeEventListener('scroll', onScroll);
	}, [showGradient]);

	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!(scroller && shouldAnimate)) {
			return () => {};
		}

		const handleInterval = () => {
			const { next, last } = findScrollTargets(scroller);

			// Stop animating once the end is reached.
			if (last && checkScrollVisibility(scroller, last).right) {
				setShouldAnimate(false);
				setShowGradient(false);
				return;
			}

			if (next) {
				// Note that this doesn't guarantee perfect alignment. The scroll-snap handles that.
				scroller.scroll({
					left: next.offsetLeft,
				});
			}
		};

		const intervalId = setInterval(handleInterval, intervalSeconds * 1_000);

		return () => {
			clearInterval(intervalId);
		};
	}, [intervalSeconds, shouldAnimate]);

	return (
		<div>
			<div
				data-testid="activity-scroller-gradient"
				className={cn('pointer-events-none absolute top-0 h-full w-full', {
					// Fades into the page background at the scroller's trailing edge. Referencing the
					// `--color-background-base` custom property directly (rather than a hardcoded `white`)
					// keeps this in sync with dark mode, since this component is always rendered directly on
					// the page body rather than inside a card.
					'bg-[linear-gradient(to_right,transparent_80%,var(--color-background-base))]': showGradient,
				})}
			/>
			<div
				ref={scrollerRef}
				// Making each child scroll-snap *:snap-start simplifies the math we have to do in the scroll handler.
				// (Account for margin, etc.)
				// Fallback [&::-webkit-scrollbar]:hidden for hiding scrollbar in older browsers:
				className={cn(
					'snap-proximity overflow-x-scroll scroll-smooth whitespace-nowrap *:snap-start [&::-webkit-scrollbar]:hidden',
					className,
				)}
				data-testid="activity-scroller"
				// These classes do not exist in tailwind, so we have to add them manually:
				style={{
					scrollbarWidth: 'none',
					msOverflowStyle: 'none',
				}}
				{...rest}
			>
				{children}
			</div>
		</div>
	);
};
